import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useOpsStore } from '../../store/ops.store';
import { Download } from 'lucide-react';


const COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#14b8a6', // teal
  '#a855f7', // purple
];

interface ChartRow {
  studioName: string;
  [programName: string]: string | number;
}

export default function ProductPerformanceChart({ isDark }: { isDark: boolean }) {
  const productPerformance = useOpsStore((s) => s.productPerformance);
  const fromDate = useOpsStore((s) => s.reportFrom);
  const toDate = useOpsStore((s) => s.reportTo);

  const { chartData, programs } = useMemo(() => {
    if (!productPerformance.length) return { chartData: [], programs: [] };

    // Collect unique program names
    const programSet = new Set<string>();
    const studioMap = new Map<string, ChartRow>();

    for (const item of productPerformance) {
      programSet.add(item.programName);

      if (!studioMap.has(item.studioName)) {
        studioMap.set(item.studioName, { studioName: item.studioName });
      }
      const row = studioMap.get(item.studioName)!;
      row[item.programName] = item.checkins;
    }

    const programs = Array.from(programSet).sort();
    const chartData = Array.from(studioMap.values()).sort((a, b) =>
      a.studioName.localeCompare(b.studioName),
    );

    return { chartData, programs };
  }, [productPerformance]);

  if (!chartData.length) {
    return (
      <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Hiệu suất Training Program <span className={`ml-2 text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>({fromDate} — {toDate})</span></h3>
        <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Số lượt check-in theo Training Program và studio trong kỳ đã chọn</p>
        <div className={`flex items-center justify-center h-64 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          Chưa có dữ liệu
        </div>
      </div>
    );
  }

  return (
    <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex items-center gap-2 mb-1">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Hiệu suất Training Program</h3>
        <div className="flex-1" />
        <button
          onClick={() => {
            const header = ['Studio', ...programs, 'Tổng'];
            const csvRows = [header.join(',')];
            for (const row of chartData) {
              const total = programs.reduce((sum, p) => sum + ((row[p] as number) || 0), 0);
              csvRows.push([
                `"${(row.studioName as string).replace(/"/g, '""')}"`,
                ...programs.map(p => (row[p] as number) || 0),
                total,
              ].join(','));
            }
            const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `product_performance_${fromDate}_${toDate}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isDark
              ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>
      <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Số lượt check-in theo Training Program và studio trong kỳ đã chọn</p>
      <ResponsiveContainer width="100%" height={window.innerWidth < 640 ? 280 : 400}>
        <BarChart data={chartData} margin={{ top: 24, right: 20, left: 0, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
          <XAxis
            dataKey="studioName"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 12 }}
            angle={-30}
            textAnchor="end"
            interval={0}
            height={60}
          />
          <YAxis tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 12 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
              borderRadius: '8px',
              color: isDark ? '#f8fafc' : '#111827',
            }}
            labelStyle={{ color: isDark ? '#f8fafc' : '#111827', fontWeight: 600 }}
          />
          <Legend
            verticalAlign="top"
            wrapperStyle={{ color: isDark ? '#cbd5e1' : '#374151', fontSize: 12, paddingBottom: 12 }}
          />
          {programs.map((program, idx) => {
            const isTop = idx === programs.length - 1;
            return (
              <Bar
                key={program}
                dataKey={program}
                stackId="a"
                fill={COLORS[idx % COLORS.length]}
                radius={isTop ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                label={isTop ? {
                  position: 'top' as const,
                  content: ({ x, y, width, index }: any) => {
                    const row = chartData[index];
                    if (!row) return null;
                    const total = programs.reduce((sum, p) => sum + ((row[p] as number) || 0), 0);
                    return (
                      <text
                        x={x + width / 2}
                        y={y - 6}
                        textAnchor="middle"
                        fill={isDark ? '#94a3b8' : '#6b7280'}
                        fontSize={11}
                        fontWeight={600}
                      >
                        {total}
                      </text>
                    );
                  },
                } : undefined}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
